import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import useBackgroundAudio from '../hooks/useBackgroundAudio';

const VoiceSessionContext = createContext(null);

export function useVoiceSession() {
  return useContext(VoiceSessionContext);
}

export function VoiceSessionProvider({ children }) {
  const [session, setSession] = useState(null);
  // session shape: { type: 'voice-room'|'chat-call'|'open-chat', id, title, lkRoom, participants, isMuted }
  const [isMuted, setIsMuted] = useState(false);
  const [speakingUsers, setSpeakingUsers] = useState(new Set());
  const lkRoomRef = useRef(null);
  const onEndRef = useRef(null);

  // Keep audio alive in background for ALL voice sessions (like YouTube Premium)
  const sessionTitle = session?.type === 'voice-room' ? '보이스룸' :
    session?.type === 'chat-call' ? '통화 중' :
    session?.type === 'open-chat' ? '음성 채팅' :
    session?.type === 'radio-host' ? '라디오 방송' :
    session?.type === 'radio' ? '라디오' : '루나문';
  useBackgroundAudio({
    title: session?.title || sessionTitle,
    artist: '루나문',
    isActive: !!session,
  });

  const startSession = useCallback(({ type, id, title, lkRoom, onEnd }) => {
    // If reconnecting to same session, preserve mute state
    const isSameSession = lkRoomRef.current === lkRoom;
    lkRoomRef.current = lkRoom;
    onEndRef.current = onEnd || null;
    setSession({ type, id, title });
    if (!isSameSession) {
      // Only reset mute for truly new sessions; sync from actual mic state
      if (lkRoom) {
        const micEnabled = lkRoom.localParticipant.isMicrophoneEnabled;
        setIsMuted(!micEnabled);
      } else {
        setIsMuted(false);
      }
      setSpeakingUsers(new Set());
    }

    // Set up voice activity detection
    if (lkRoom) {
      import('livekit-client').then(({ RoomEvent }) => {
        lkRoom.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
          setSpeakingUsers(new Set(speakers.map(s => s.identity)));
        });
      });
    }
  }, []);

  const endSession = useCallback(() => {
    if (onEndRef.current) {
      try { onEndRef.current(); } catch {}
    }
    if (lkRoomRef.current) {
      try { lkRoomRef.current.disconnect(); } catch {}
    }
    lkRoomRef.current = null;
    onEndRef.current = null;
    setSession(null);
    setIsMuted(false);
    setSpeakingUsers(new Set());
  }, []);

  const toggleMute = useCallback(() => {
    if (!lkRoomRef.current) return;
    const newMuted = !isMuted;
    lkRoomRef.current.localParticipant.setMicrophoneEnabled(!newMuted);
    setIsMuted(newMuted);
  }, [isMuted]);

  // Sync mute state from the actual LiveKit track (e.g. on re-entering the page)
  const syncMuteFromLiveKit = useCallback(() => {
    if (!lkRoomRef.current) return;
    const micEnabled = lkRoomRef.current.localParticipant.isMicrophoneEnabled;
    setIsMuted(!micEnabled);
  }, []);

  const isSpeaking = useCallback((userId) => {
    return speakingUsers.has(String(userId));
  }, [speakingUsers]);

  return (
    <VoiceSessionContext.Provider value={{
      session, lkRoom: lkRoomRef.current, isMuted,
      speakingUsers, isSpeaking,
      startSession, endSession, toggleMute, syncMuteFromLiveKit, setIsMuted,
    }}>
      {children}
    </VoiceSessionContext.Provider>
  );
}
